// Hideo - data store (Pinia): the profiles list and the active profile's data.
// The stored formats are exactly the old ones (docs/data.md): same localStorage keys, same JSON.
// The active profile's data is kept in a shallowRef: the code mutates it in place, so it must not be wrapped in a deep
// reactive proxy; `rev` changes at every save, for components to react.
// normalizeData runs when the data enters the store (first read, setData at every save, a full redraw: invalidate), not
// at every read: it goes over the whole profile, and some lists read the data hundreds of times per redraw. Between two
// saves the same normalised object is returned (its arrays and objects stay the same, so references taken from it stay
// valid).
import { defineStore } from 'pinia'
import { reactive, ref, shallowRef } from 'vue'
import { normalizeData } from '../data/normalize'
import { emptyData } from '../data/demo'
import { loadLocal, loadProfiles, saveProfilesLocal, writeLocal } from '../data/profiles'
import type { ProfileData, ProfilesState } from '../data/types'

export const useDataStore = defineStore('data', () => {
  const profiles = reactive<ProfilesState>(loadProfiles())
  const raw = shallowRef<Record<string, unknown> | null>(null)
  const rev = ref(0)
  /** The raw object that was normalised last (null: normalise at the next read) */
  let normalized: object | null = null

  const currentProfile = () => profiles.list.find(x => x.id === profiles.current)

  /** The active profile's data, normalised (loaded from this browser the first time) */
  function getData(): ProfileData {
    if (!raw.value) raw.value = (loadLocal(profiles.current) as Record<string, unknown>) || emptyData(profiles.current)
    if (normalized !== raw.value) { normalizeData(raw.value); normalized = raw.value }
    return raw.value as unknown as ProfileData
  }
  /** Replaces the active profile's data in memory (null = reload it from storage at the next read); normalised again at
   *  the next read, even when it is the same object changed in place */
  function setData(d: object | null): void {
    raw.value = d as Record<string, unknown> | null
    normalized = null
    rev.value++
  }
  /** The data was changed in place without setData: normalise it again at the next read (the full redraw calls it) */
  function invalidate(): void { normalized = null }
  /** Writes the active profile's data to this browser (throws if the storage is full) */
  function writeCurrentLocal(): void {
    writeLocal(profiles.current, raw.value)
  }
  /** Any profile's data: normalised copy from this browser, or its starting data */
  function readProfileLocal(id: string): ProfileData {
    return normalizeData(loadLocal(id) || emptyData(id))
  }
  function saveProfiles(): void {
    saveProfilesLocal(profiles)
  }

  return { profiles, raw, rev, currentProfile, getData, setData, invalidate, writeCurrentLocal, readProfileLocal, saveProfiles }
})
